<script setup>
import SlideOver from '@/components/SlideOver.vue';
import { useForm } from '@inertiajs/vue3';
import CharCounter from '@/components/Forms/CharCounter.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import BadgePill from '@/components/BadgePill.vue';
import { IconCalendarOff } from '@tabler/icons-vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import AlertMessage from '@/components/AlertMessage.vue';
import AppWell from '@/Shared/AppWell.vue';
import AppCard from '@/Shared/AppCard.vue';

const props = defineProps({
    turn: {
        type: Object,
        required: false,
    },
    students: {
        type: Array,
        required: false,
    },
    options: {
        type: Object,
        required: true,
    },
});

// const turn = props.turn;
const emit = defineEmits(['close']);
const close = () => {
    emit('close');
};

const form = useForm({
    comment: props.turn.comment,
    student_id: props.turn.student_id,
    channel: props.turn.channel,
});

function submit() {
    form.post(props.turn.links.store, {
        onSuccess: () => {
            close();
        },
    });
}
</script>

<template>
    <SlideOver :show="!!turn" @close="close">
        <div class="flex justify-between">
            <span class="truncate font-medium">
                {{ turn.group.name }}
            </span>
            <time :datetime="turn.datetime" class="text-gray-500"> {{ turn.time_start }} - {{ turn.time_end }} </time>
        </div>

        <div v-if="turn.not_requestable_reason">
            <AlertMessage class="mt-3 inline-block" type="alert">
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>
                <template v-slot:body> {{ turn.not_requestable_reason }} </template>
            </AlertMessage>
        </div>

        <div v-if="turn.status === 'blocked'">
            <div class="flex flex-col items-center">
                <div>
                    <IconCalendarOff class="text-gray-400 w-10 h-10" />
                </div>
                <div>
                    <BadgePill color="gray">
                        {{ $t('messages.not_available') }}
                    </BadgePill>
                </div>
            </div>
        </div>
        <div v-else-if="turn.status === 'reserved'">
            <div>
                <BadgePill :color="turn.reserved_by_user ? 'blue' : 'gray'">
                    {{ $t('messages.meeting_turn_reserved') }}
                </BadgePill>
            </div>
            <div v-if="turn.reserved_by_user">
                <div class="mt-1">{{ $t('messages.student') }}: {{ turn.student_name }}</div>
                <div class="mt-1">{{ $tc('messages.channels', 1) }}: {{ turn.channel_display }}</div>
                <div class="mt-1">{{ $t('messages.requested_by') }}: {{ turn.requester_name }}</div>
                <div v-if="turn.comment" class="mt-1">
                    <AppWell>
                        <AppCard>
                            <span class="p-1">{{ turn.comment }}</span>
                        </AppCard>
                    </AppWell>
                </div>
            </div>
        </div>
        <form method="post" @submit.prevent="submit" v-else-if="turn.can.create">
            <FormError :value="form.errors.turn" class="mt-2" />
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="col-span-12">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.student')" for="student_id" />
                        <ul class="mt-1">
                            <li
                                v-for="student in students"
                                :key="student.value"
                                class="p-1"
                                :class="{
                                    'border-indigo-600 ring-2 ring-indigo-600 rounded-full':
                                        student.value === form.student_id,
                                }"
                            >
                                <label>
                                    <div class="flex space-x-2 items-center">
                                        <img
                                            :src="student.avatar_or_default"
                                            :alt="student.text"
                                            class="rounded-full h-10"
                                        />
                                        <div>{{ student.text }}</div>
                                    </div>
                                    <input
                                        type="radio"
                                        name="student_id"
                                        v-model="form.student_id"
                                        :value="student.value"
                                        class="sr-only"
                                    />
                                </label>
                            </li>
                        </ul>
                        <FormError :value="form.errors.student_id" class="mt-2" />
                    </div>
                    <div v-auto-animate>
                        <AlertMessage class="mt-5 inline-block" type="info" v-if="form.student_id === null">
                            <template v-slot:title>
                                {{ $t('messages.information') }}
                            </template>
                            <template v-slot:body> {{ $t('messages.select') }}: {{ $t('messages.student') }} </template>
                        </AlertMessage>
                    </div>
                </div>

                <div class="sm:col-span-12">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.channels', 1)" for="status" />
                        <FormSelect
                            required
                            id="gradable"
                            v-model="form.channel"
                            :options="options.channels"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.channel" class="mt-2" />
                    </div>
                </div>

                <div class="col-span-12">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.comment')" for="comment">
                            <template v-slot:corner>
                                <CharCounter
                                    :value="form.comment"
                                    :max="240"
                                    show-max
                                    class="text-sm font-medium leading-5 text-gray-500"
                                />
                            </template>
                        </FormLabel>
                        <FormTextarea
                            id="comment"
                            v-model="form.comment"
                            name="comment"
                            maxlength="240"
                            class="mt-1 w-full"
                            :placeholder="$t('messages.(optional)')"
                        />
                        <FormError :value="form.errors.comment" class="mt-2" />
                    </div>
                </div>
            </div>
            <div class="flex justify-end mt-3">
                <SubmitFormButton :button-text="$t('messages.request_meeting')" :processing="form.processing" />
            </div>
        </form>
    </SlideOver>
</template>
